import React, { FC, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useReactiveVar } from '@apollo/client'

import { useLoading, useServerError } from 'shared/hooks'
import { TRouterParams } from 'shared/types'

import { GET_FACT_SOURCES, GET_PRODUCTS_BY_CAMPAIGN } from 'modulesCommon/graphql/queries'

import { productsFactsVar } from 'modules/Products/store'
import {
  activeProjectVar,
  chartFreshnessVar,
  countryCodeVar,
  factSourcesVar,
  globalProjectIdVar,
  projectProductsTotalVar
} from 'modulesCommon/store'
import { sortProductsByReleaseDate } from 'shared/utils/products'
import { ConditionRender } from 'components'
import ProductItems from 'modules/Products/components/ProductItems/ProductItems'
import { filterFactSources } from 'shared/utils/factSources'
import { SkeletonProducts } from 'modulesCommon/Skeleton/ProjectDetails'
import { useZoneTag } from 'modulesCommon/serviceHooks'

const Products: FC<{ isExtended?: boolean; isCompact?: boolean }> = ({ isExtended = false, isCompact = false }) => {
  const { id: globalId } = useParams<TRouterParams>()
  const globalProjectId = useReactiveVar(globalProjectIdVar)
  const id = globalId || globalProjectId

  const products = useReactiveVar(productsFactsVar)
  const countryCode = useReactiveVar(countryCodeVar)
  const projectProductsTotal = useReactiveVar(projectProductsTotalVar)
  const factSources = useReactiveVar(factSourcesVar)
  const chartFreshness = useReactiveVar(chartFreshnessVar)
  const activeProject = useReactiveVar(activeProjectVar)

  const { isSameLabel, isSpace, label } = useZoneTag(activeProject.label_id)

  const sourcesKeys = filterFactSources(factSources, countryCode)
  const sources = useMemo(() => [...new Set(sourcesKeys.map((source) => source.chart))], [sourcesKeys])

  const {
    data: productsData,
    loading: productsLoading,
    error: productsError
  } = useQuery(GET_PRODUCTS_BY_CAMPAIGN, {
    variables: { campaignId: id, countryCode }
  })

  const {
    data: factSourcesData,
    loading: factSourcesLoading,
    error: factSourcesError
  } = useQuery(GET_FACT_SOURCES, { variables: { countryCode: countryCode } })

  useEffect(() => {
    if (productsData) {
      productsFactsVar(sortProductsByReleaseDate([...(productsData?.getProductsByCampaignId || [])]))
      projectProductsTotalVar(productsData?.getProductsByCampaignId.length)
    }
  }, [productsData])

  useEffect(() => {
    if (factSourcesData) {
      factSourcesVar(factSourcesData.getFactSources)
    }
  }, [factSourcesData])

  useEffect(() => {
    if (factSourcesData) {
      chartFreshnessVar(factSourcesData.getLatestChartsByCountryCode)
    }
  }, [factSourcesData])

  useLoading({ isLoading: productsLoading || factSourcesLoading })
  useServerError([productsError, factSourcesError])

  if (!projectProductsTotal && productsLoading) return <SkeletonProducts />

  return (
    <ConditionRender
      condition={!!projectProductsTotal}
      component={
        <ProductItems
          products={products}
          isExtended={isExtended}
          isCompact={isCompact}
          factSources={factSources}
          sources={sources}
          countryCode={countryCode}
          chartFreshness={chartFreshness}
          label={isSpace ? label : undefined}
          isSameLabel={isSameLabel}
        />
      }
    />
  )
}

export default Products
